import Title from '@cohort/components-xps/components/Title';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import {Loader} from '@cohort/wallet/components/Loader';
import BaseModal from '@cohort/wallet/components/modals/BaseModal';
import DrawerModal from '@cohort/wallet/components/modals/DrawerModal';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import {ownershipsKeys} from '@cohort/wallet/hooks/api/Ownerships';
import useNotify from '@cohort/wallet/hooks/notify';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import {getMerchantSpaceRoute} from '@cohort/wallet/lib/Pages';
import {Colors} from '@cohort/wallet/lib/Theme';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {CheckCircle, X} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

const AUTO_REFRESH_DELAY_MS = 2000;

type NftTransferringModalProps = {
  ownership: OwnershipWDto;
};

type ContentData = {
  icon: JSX.Element;
  title: string;
  subtitle: string;
};

const NftTransferringModal: React.FC<NftTransferringModalProps> = ({ownership}) => {
  const [liveOwnership, setLiveOwnership] = useState(ownership);
  const {t} = useTranslation('components', {
    keyPrefix: 'modals.transfer.nftTransferringModal',
  });
  const getOwnership = useApi(OwnershipsApi.getOwnership);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotify();
  const {isMobile} = useScreenSizeContext();
  const {hasDarkBg} = useThemeContext();

  const onClick = (): void => {
    queryClient.invalidateQueries(ownershipsKeys.ownerships);
    navigate(getMerchantSpaceRoute());
  };

  useEffect(() => {
    let refreshOwnershipTimeout: number | undefined;

    function refreshOwnership(): void {
      refreshOwnershipTimeout = undefined;
      getOwnership(ownership.id)
        .then(ownership => {
          setLiveOwnership(ownership);
          if (ownership.status === 'transferring') {
            refreshOwnershipTimeout = window.setTimeout(refreshOwnership, AUTO_REFRESH_DELAY_MS);
          }
        })
        .catch(err => notify('error', err));
    }

    refreshOwnership();
    return () => {
      if (refreshOwnershipTimeout !== undefined) {
        clearTimeout(refreshOwnershipTimeout);
      }
    };
  }, [getOwnership, ownership.id, notify]);

  const {transfer} = liveOwnership;
  const {icon, title, subtitle} = useMemo((): ContentData => {
    if (transfer === null) {
      return {
        icon: <X className="h-10 w-10 text-red-500" />,
        title: t('failedTitle'),
        subtitle: t('failedContent'),
      };
    }

    const {status, recipientAddress} = transfer;
    if (status === 'failed') {
      return {
        icon: <X className="h-10 w-10 text-red-500" />,
        title: t('failedTitle'),
        subtitle: t('failedContent'),
      };
    } else if (status === 'processing' || status === 'queued') {
      return {
        icon: (
          <div className="relative h-10 w-10">
            <Loader color={hasDarkBg ? Colors.White : Colors.Black} />
          </div>
        ),
        title: t('pendingTitle'),
        subtitle: t('pendingContent'),
      };
      // status === 'success'
    } else {
      return {
        icon: <CheckCircle className="h-10 w-10 text-green-500" />,
        title: t('successTitle'),
        subtitle: t('successContent', {address: recipientAddress}),
      };
    }
  }, [transfer, t, hasDarkBg]);

  const CloseButton = (
    <div className="text-right">
      <SecondaryButton
        text={t('button')}
        onClick={onClick}
        tracking={{
          namespace: 'modals.digitalAssetTransfer.close',
          metadata: {ownershipId: ownership.id},
        }}
      />
    </div>
  );

  return isMobile ? (
    <DrawerModal tracking={{namespace: 'modals.digitalAssetTransfer'}}>
      <CenteredContent>
        <div className="flex items-center gap-2">
          {icon}
          <Title content={title} classes="text-2xl font-semibold" />
        </div>
        <Subtitle content={subtitle} classes="!text-base font-medium" />
        {CloseButton}
      </CenteredContent>
    </DrawerModal>
  ) : (
    <BaseModal
      title={
        <div className="flex items-center gap-2">
          {icon}
          <Title content={title} classes="text-2xl font-semibold" />
        </div>
      }
      subtitle={<Subtitle content={subtitle} classes="!text-base font-medium" />}
      tracking={{namespace: 'modals.digitalAssetTransfer'}}
    >
      {CloseButton}
    </BaseModal>
  );
};

export default NftTransferringModal;
